import JSZip from 'jszip';
import { Template } from '../types/template';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';

/**
 * Dokumentumgeneráló segédosztály, ami a sablondokumentumokban lévő placeholdereket
 * helyettesíti az adatokkal, és dokumentumot hoz létre
 */
export class DocGenerator {
  /**
   * Generál egy vagy több dokumentumot a megadott sablon alapján, adatokkal kitöltve
   * 
   * @param template A használt dokumentum sablon
   * @param data A behelyettesítendő adatok tömbje (soronként)
   * @param formats A kimeneti formátumok tömbje (pl. ['docx', 'pdf'])
   * @returns Promise az elkészült dokumentumok Blob tömbjeivel, formátum szerint csoportosítva
   */
  static async generateDocuments(
    template: Template,
    data: Record<string, string>[],
    formats: string[]
  ): Promise<Record<string, Blob[]>> {
    try {
      if (!template.fileUrl) {
        throw new Error('A sablon dokumentumnak nincs elérhető URL-je.');
      }

      // Letöltjük a sablon dokumentumot
      const response = await fetch(template.fileUrl);
      if (!response.ok) {
        throw new Error(`Nem sikerült letölteni a dokumentumot: ${response.status} ${response.statusText}`);
      }

      const templateBuffer = await response.arrayBuffer();

      // Minden adatsorra generálunk egy dokumentumot
      const generatedDocs = await Promise.all(data.map(async (rowData, index) => {
        // Intelligens fájlnév generálás a dokumentum tartalma alapján
        let fileName = '';
        
        // Szerződés esetén a szerződésszámot használjuk
        if (rowData['Szerződés száma']) {
          fileName = rowData['Szerződés száma'];
        }
        // Árajánlat esetén az árajánlat számot használjuk
        else if (rowData['Ajánlat száma']) {
          fileName = rowData['Ajánlat száma'];
        }
        // Customer ID fallback (ha a fentiek nincsenek)
        else if (rowData['customerId']) {
          fileName = `customer_${rowData['customerId']}`;
        }
        // Template név fallback
        else if (template.name) {
          fileName = `${template.name}_${index + 1}`;
        }
        // Végső fallback
        else {
          fileName = `document_${index + 1}`;
        }
        
        console.log('🏷️ Generated filename:', fileName, 'from data:', {
          contractNumber: rowData['Szerződés száma'],
          quotationNumber: rowData['Ajánlat száma'],
          customerId: rowData['customerId'],
          templateName: template.name
        });

        return this.generateSingleDocument(templateBuffer, rowData, fileName, formats);
      }));

      // Összegyűjtjük a dokumentumokat formátum szerint
      const result: Record<string, Blob[]> = {};
      formats.forEach(format => {
        result[format] = [];
      });

      // Összegyűjtjük a dokumentumokat formátum szerint
      generatedDocs.forEach(docGroup => {
        Object.entries(docGroup).forEach(([format, blob]) => {
          if (result[format]) {
            result[format].push(blob);
          }
        });
      });

      return result;
    } catch (error) {
      console.error('Hiba a dokumentumok generálása közben:', error);
      throw error;
    }
  }

  /**
   * DOCX dokumentum konvertálása PDF formátumba frontend-en (docx-preview + html2canvas + jspdf)
   * 
   * @param docxBlob A DOCX dokumentum blob-ja
   * @param fileName A dokumentum neve kiterjesztés nélkül
   * @returns Promise a PDF Blob objektumával
   */
  private static async convertDocxToPdfWithGoogleDocs(docxBlob: Blob, fileName: string): Promise<Blob> {
    try {
      console.log('Frontend PDF generálás megkezdése...');
      
      // Dinamikus import a docx-preview, html2canvas és jspdf számára
      const [{ renderAsync }, html2canvas, jsPDF] = await Promise.all([
        import('docx-preview'),
        import('html2canvas'),
        import('jspdf')
      ]);
      
      // Létrehozunk egy rejtett konténert a DOCX rendereléshez
      const container = document.createElement('div');
      container.style.position = 'absolute';
      container.style.left = '-9999px';
      container.style.top = '0';
      container.style.width = '210mm'; // A4 szélesség
      container.style.background = 'white';
      container.style.padding = '20mm';
      document.body.appendChild(container);
      
      try {
        // DOCX renderelése HTML-be
        console.log('DOCX renderelése HTML-be...');
        await renderAsync(docxBlob, container, undefined, {
          className: 'docx-preview',
          inWrapper: true,
          ignoreWidth: false,
          ignoreHeight: false,
          ignoreFonts: false,
          breakPages: true,
          ignoreLastRenderedPageBreak: false,
          experimental: false,
          trimXmlDeclaration: true,
          useBase64URL: false,
          renderHeaders: true,
          renderFooters: true,
          renderFootnotes: true,
          renderEndnotes: true,
        });
        
        // Kis késleltetés, hogy a renderelés befejeződjön
        await new Promise(resolve => setTimeout(resolve, 500));
        
        console.log('HTML → Canvas konverzió...');
        
        // HTML → Canvas
        const canvas = await html2canvas.default(container, {
          scale: 2, // Jobb minőség
          useCORS: true,
          logging: false,
          backgroundColor: '#ffffff',
          windowWidth: 794, // A4 szélesség pixelben (210mm @ 96 DPI)
          windowHeight: 1123, // A4 magasság pixelben (297mm @ 96 DPI)
        });
        
        console.log('Canvas → PDF konverzió...');
        
        // Canvas → PDF
        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF.default({
          orientation: 'portrait',
          unit: 'mm',
          format: 'a4',
          compress: true,
        });
        
        const imgWidth = 210; // A4 szélesség mm-ben
        const pageHeight = 297; // A4 magasság mm-ben
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        let heightLeft = imgHeight;
        let position = 0;
        
        // Első oldal
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight, undefined, 'FAST');
        heightLeft -= pageHeight;
        
        // További oldalak, ha szükséges
        while (heightLeft > 0) {
          position = heightLeft - imgHeight;
          pdf.addPage();
          pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight, undefined, 'FAST');
          heightLeft -= pageHeight;
        }
        
        console.log('PDF generálás kész!');
        
        // PDF Blob létrehozása
        const pdfBlob = pdf.output('blob');
        
        return pdfBlob;
      } finally {
        // Takarítás - konténer eltávolítása
        document.body.removeChild(container);
      }
    } catch (error) {
      console.error('Hiba a frontend PDF konvertálás során:', error);
      throw error;
    }
  }

  /**
   * Egy dokumentum generálása egy adatsorból
   * 
   * @param templateBuffer A sablon dokumentum bináris tartalma
   * @param data A behelyettesítendő adatok (kulcs-érték párok)
   * @param fileName A kimeneti fájl neve (kiterjesztés nélkül)
   * @param formats A kimeneti formátumok tömbje (pl. ['docx', 'pdf'])
   * @returns Promise az elkészült dokumentumok Blob objektumaival, formátum szerint
   */
  private static async generateSingleDocument(
    templateBuffer: ArrayBuffer,
    data: Record<string, string>,
    fileName: string,
    formats: string[]
  ): Promise<Record<string, Blob>> {
    try {
      // Betöltjük a sablont
      const zip = new JSZip();
      const docx = await zip.loadAsync(templateBuffer);

      // XML ampersand kódolása - csak & karaktert kezeljük
      const escapeAmpersands = (text: string): string => {
        if (!text) return '';
        return text.replace(/&/g, '&amp;');
      };

      // Készítsünk másolatot az eredeti adatokról
      const processedData = { ...data };
      
      // Végigmegyünk az összes adaton és előfeldolgozzuk az ampersand karaktereket
      for (const [key, value] of Object.entries(processedData)) {
        if (typeof value === 'string' && value.includes('&')) {
          processedData[key] = escapeAmpersands(value);
          console.log(`Ampersand (&) kódolás: ${key}`);
        }
      }

      // Dátum formázó segédfüggvény - Magyar formátumra alakít
      const formatHungarianDate = (dateStr: string): string => {
        if (!dateStr) return dateStr;
        
        // YYYY-MM-DDTHH:MM formátum kezelése (datetime-local inputból)
        const isoDateTimeMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::\d{2})?(?:\.\d+)?(?:Z)?$/);
        if (isoDateTimeMatch) {
          const [_, year, month, day, hour, minute] = isoDateTimeMatch;
          return `${year}.${month}.${day}. ${hour}:${minute}`;
        }
        
        // YYYY-MM-DD formátum átalakítása YYYY.MM.DD. formátumra
        const isoMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (isoMatch) {
          const [_, year, month, day] = isoMatch;
          return `${year}.${month}.${day}.`;
        }
        
        // YYYY.MM.DD formátum átalakítása YYYY.MM.DD. formátumra (ha még nincs pont a végén)
        const dotMatch = dateStr.match(/^(\d{4})\.(\d{2})\.(\d{2})$/);
        if (dotMatch) {
          return `${dateStr}.`;
        }
        
        // Ha már helyes formátumban van ponttal a végén
        if (/^\d{4}\.\d{2}\.\d{2}\.$/.test(dateStr)) {
          return dateStr;
        }
        
        // Egyéb dátumformátumok kezelése
        try {
          // Megpróbáljuk Date objektumként értelmezni, ha értelmezhető
          const date = new Date(dateStr);
          if (!isNaN(date.getTime())) {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            
            // Ellenőrizzük, hogy van-e időpont komponens is
            if (dateStr.includes('T') || dateStr.includes(' ')) {
              const hour = String(date.getHours()).padStart(2, '0');
              const minute = String(date.getMinutes()).padStart(2, '0');
              return `${year}.${month}.${day}. ${hour}:${minute}`;
            }
            
            return `${year}.${month}.${day}.`;
          }
        } catch (e) {
          console.warn('Nem sikerült a dátumformázás:', e);
        }
        
        // Ha nem sikerült átalakítani, visszaadjuk az eredetit
        return dateStr;
      };
      
      // Előfeldolgozás - formázzuk a dátum mezőket
      const dateFields = [
        'Szerződés kezdete', 
        'Szerződés vége', 
        'Kelt',
        'Dátum',
        'Aláírás dátuma',
        'Kiállítás dátuma',
        'Ajánlat érvényessége'
      ];
      
      // Formázzuk a dátum mezőket
      dateFields.forEach(field => {
        if (processedData[field]) {
          processedData[field] = formatHungarianDate(processedData[field]);
          console.log(`Dátum formázás: ${field} = ${processedData[field]}`);
        }
      });
      
      // Továbbá ellenőrizzünk minden más mezőt is, hogy dátumot tartalmaz-e
      for (const [key, value] of Object.entries(processedData)) {
        if (!dateFields.includes(key) && typeof value === 'string') {
          // Ellenőrizzük, hogy dátumot tartalmaz-e (YYYY-MM-DD vagy YYYY.MM.DD formátumban)
          if (/^\d{4}-\d{2}-\d{2}$/.test(value) || /^\d{4}\.\d{2}\.\d{2}$/.test(value)) {
            processedData[key] = formatHungarianDate(value);
            console.log(`Automatikus dátum felismerés és formázás: ${key} = ${processedData[key]}`);
          }
        }
      }

      // Fontos mezők előzetes ellenőrzése
      // Különös figyelmet fordítunk a gyakran használt mezőkre, hogy biztosan helyesen legyenek kitöltve
      const criticalFields = [
        'Szerződés vége', 
        'Szerződés kezdete', 
        'Szerződés száma',
        'POD',
        'Cég megnevezése',
        'Cég adószáma',
        'Cégjegyzékszám'
      ];
      
      // Naplózzuk a kritikus mezők értékeit
      criticalFields.forEach(field => {
        console.log(`Kritikus mező "${field}" értéke: "${processedData[field] || 'nincs megadva'}"`);
      });

      // Minden XML fájlt feldolgozunk
      const xmlFiles = Object.keys(docx.files).filter(fileName =>
        fileName.startsWith("word/") &&
        fileName.endsWith(".xml")
      );

      console.log(`Feldolgozandó fájlok: ${xmlFiles.length} db XML`);

      for (const xmlFileName of xmlFiles) {
        let content = await docx.file(xmlFileName)?.async("string");
        if (!content) continue;

        // Speciális kezelés a POD_TABLE helyőrzőnek
        if (content.includes('{{POD_TABLE}}') || content.includes('{{ POD_TABLE }}')) {
          // POD táblázat speciális kezelése
          if (processedData['POD_TABLE']) {
            content = content.replace(/\{\{POD_TABLE\}\}/g, processedData['POD_TABLE']);
            content = content.replace(/\{\{ POD_TABLE \}\}/g, processedData['POD_TABLE']);
          }
        }

        // Speciális kezelés speciális formátumokhoz, mint a szerződéses időtartam
        if (content) {
          // Speciális időtartam formátum kezelése
          if (processedData['Szerződés kezdete'] && processedData['Szerződés vége']) {
            const szerzodesKezdete = processedData['Szerződés kezdete'];
            const szerzodesvege = processedData['Szerződés vége'];
            
            // Különböző formátumok kezelése (tól-ig, időpontokkal együtt)
            const patterns = [
              // Konkrét formátum: {{Szerződés kezdete}} 00:00-tól {{Szerződés vége}} 24:00-ig
              {
                regex: /\{\{Szerződés kezdete\}\}[ ]*00:00-tól[ ]*\{\{Szerződés vége\}\}[ ]*24:00-ig/g,
                replacement: `${szerzodesKezdete} 00:00-tól ${szerzodesvege} 24:00-ig`
              },
              // Szóközös formátum konkrét időpontokkal
              {
                regex: /\{\{ Szerződés kezdete \}\}[ ]*00:00-tól[ ]*\{\{ Szerződés vége \}\}[ ]*24:00-ig/g,
                replacement: `${szerzodesKezdete} 00:00-tól ${szerzodesvege} 24:00-ig`
              },
              // Alap tól-ig minta
              {
                regex: /\{\{Szerződés kezdete\}\}(.*?)-t[oó]l(.*?)\{\{Szerződés vége\}\}(.*?)(-ig)?/g,
                replacement: `${szerzodesKezdete}$1-tól$2${szerzodesvege}$3$4`
              },
              // Szóközös tól-ig minta
              {
                regex: /\{\{ ?Szerződés kezdete ?\}\}(.*?)-t[oó]l(.*?)\{\{ ?Szerződés vége ?\}\}(.*?)(-ig)?/g,
                replacement: `${szerzodesKezdete}$1-tól$2${szerzodesvege}$3$4`
              },
              // XML töredezett formátum (gyakran előfordul a Word XML-ben)
              {
                regex: /\{\{Szerződés kezdete\}\}([^{]*?)-([^{]*?)\{\{Szerződés vége\}\}/g,
                replacement: `${szerzodesKezdete}$1-$2${szerzodesvege}`
              }
            ];
            
            // Még speciálisabb eset - a képen látható példa
            if (content.includes('{{Szerződés kezdete}}') && content.includes('00:00-tól') && 
                content.includes('{{Szerződés vége}}') && content.includes('24:00-ig')) {
              
              // Konkrét helyettesítés a teljes XML tartalomra - ez a legbiztosabb módszer
              // Először próbáljuk menteni a szöveg "nyers" változatát (strip XML tags)
              const textContent = content.replace(/<[^>]+>/g, ' ');
              
              if (textContent.includes('{{Szerződés kezdete}}') && textContent.includes('{{Szerződés vége}}')) {
                console.log('Speciális időtartam minta található:', 
                  textContent.substring(
                    Math.max(0, textContent.indexOf('{{Szerződés kezdete}}') - 10),
                    Math.min(textContent.length, textContent.indexOf('24:00-ig') + 15)
                  )
                );
              }
              
              // Direkt helyettesítés a képen látott mintára - figyelünk az XML tagekre is
              // Két regexp: egyik ami figyelembe veszi az XML tageket, másik ami nem
              const directPatternsWithXml = [
                // Alapformátum
                {
                  regex: /\{\{Szerződés kezdete\}\}([^{]*?)00:00([^{]*?)-tól([^{]*?)\{\{Szerződés vége\}\}([^{]*?)24:00([^{]*?)-ig/g,
                  replacement: `${szerzodesKezdete}$100:00$2-tól$3${szerzodesvege}$424:00$5-ig`
                },
                // XML tagekkel szabdalt formátum 
                {
                  regex: /\{\{Szerződés kezdete\}\}([^{<]*?)00:00([^{<]*?)-t[oó]l([^{<]*?)\{\{Szerződés vége\}\}([^{<]*?)24:00([^{<]*?)-ig/g,
                  replacement: `${szerzodesKezdete}$100:00$2-tól$3${szerzodesvege}$424:00$5-ig`
                },
                // A képen látható pontos formátum
                {
                  regex: /\{\{Szerződés kezdete\}\}([^{]*?)00:00-tól([^{]*?)\{\{Szerződés vége\}\}([^{]*?)24:00-ig/g,
                  replacement: `${szerzodesKezdete}$100:00-tól$2${szerzodesvege}$324:00-ig`
                },
                // Új formátum: a dátum és időpont egymás mellett van
                {
                  regex: /\{\{Szerződés kezdete\}\}([^{]*?)00:00([^{]*?)\{\{Szerződés vége\}\}([^{]*?)24:00/g,
                  replacement: `${szerzodesKezdete}$100:00$2${szerzodesvege}$324:00`
                },
                // További variáció az új formátumra
                {
                  regex: /([^\}]+) 00:00([^{]*?)\{\{Szerződés vége\}\}([^{]*?)24:00/g,
                  replacement: `$1 00:00$2${szerzodesvege}$324:00`
                },
                // Konkrétan a felhasználó által megadott minta
                {
                  regex: /(\d{4}-\d{2}-\d{2}) 00:00([^{]*?)\{\{Szerződés vége\}\}([^{]*?)24:00/g,
                  replacement: `$1 00:00$2${szerzodesvege}$324:00`
                }
              ];
              
              // Közvetlenül a képen látható formátumra
              if (content) {
                directPatternsWithXml.forEach(pattern => {
                  if (pattern.regex.test(content!)) {
                    content = content!.replace(pattern.regex, pattern.replacement);
                    console.log('Sikeresen helyettesített időtartam minta:', pattern.regex);
                  }
                });

                // Külön kezelés az egymástól elválasztott dátum-időpont párokhoz
                if (content.includes('{{Szerződés vége}}') && (content.includes('00:00') || content.includes('24:00'))) {
                  console.log('Speciális dátum-idő kombináció azonosítva');
                  
                  // Dátum-idő kombináció keresése
                  const datumIdoMintak = [
                    // YYYY-MM-DD 00:00 ... {{Szerződés vége}} 24:00
                    {
                      regex: /(\d{4}-\d{2}-\d{2})\s+00:00\s+\{\{Szerződés vége\}\}\s+24:00/g,
                      replacement: `$1 00:00   ${szerzodesvege} 24:00`
                    },
                    // YYYY.MM.DD 00:00 ... {{Szerződés vége}} 24:00
                    {
                      regex: /(\d{4}\.\d{2}\.\d{2})\s+00:00\s+\{\{Szerződés vége\}\}\s+24:00/g,
                      replacement: `$1 00:00   ${szerzodesvege} 24:00`
                    },
                    // {{Szerződés kezdete}} 00:00 ... YYYY-MM-DD 24:00
                    {
                      regex: /\{\{Szerződés kezdete\}\}\s+00:00\s+(\d{4}-\d{2}-\d{2})\s+24:00/g,
                      replacement: `${szerzodesKezdete} 00:00   $1 24:00`
                    }
                  ];
                  
                  datumIdoMintak.forEach(pattern => {
                    if (pattern.regex.test(content!)) {
                      content = content!.replace(pattern.regex, pattern.replacement);
                      console.log('Sikeresen helyettesített dátum-idő minta');
                    }
                  });
                  
                  // Még specifikusabb felismerés a bemutatott példához
                  // "2025-05-19 00:00   {{Szerződés vége}}  24:00"
                  const specificRegex = /(\d{4}-\d{2}-\d{2})\s+00:00\s+\{\{Szerződés vége\}\}\s+24:00/g;
                  if (specificRegex.test(content)) {
                    // Erőszakosabb helyettesítés több kísérlettel
                    // 1. kísérlet - pontos helyettesítés
                    content = content.replace(specificRegex, `$1 00:00   ${szerzodesvege} 24:00`);
                    
                    // 2. kísérlet - XML tageket figyelembe vevő (lazább) felismerés
                    const taggedContent = content;
                    const dateMatches = Array.from(taggedContent.matchAll(/(\d{4}-\d{2}-\d{2})/g));
                    
                    for (const dateMatch of dateMatches) {
                      const dateStr = dateMatch[1];
                      const startPos = taggedContent.indexOf(dateStr);
                      if (startPos === -1) continue;
                      
                      // A dátum utáni szövegrészben keresd {{Szerződés vége}}-t
                      const afterDateText = taggedContent.substring(startPos + dateStr.length);
                      const szerzodesVegePos = afterDateText.indexOf('{{Szerződés vége}}');
                      
                      if (szerzodesVegePos > -1 && szerzodesVegePos < 100) { // Ha viszonylag közel van
                        // Ellenőrizzük, hogy van-e "00:00" és "24:00" ebben a részben
                        const section = taggedContent.substring(
                          startPos, 
                          startPos + dateStr.length + szerzodesVegePos + 50
                        );
                        
                        if (section.includes('00:00') && section.includes('24:00')) {
                          console.log('Pontos szerződési időtartam szekció azonosítva:', section);
                          
                          try {
                            // Keressük a teljes mintát
                            const indexOfStart = startPos;
                            const indexOfEnd = taggedContent.indexOf('24:00', startPos + dateStr.length) + 5;
                            
                            if (indexOfEnd > indexOfStart) {
                              const entirePattern = taggedContent.substring(indexOfStart, indexOfEnd);
                              console.log('Teljes minta:', entirePattern);
                              
                              // Építsük fel a helyettesítő szöveget
                              const replacement = `${dateStr} 00:00   ${szerzodesvege} 24:00`;
                              
                              // Helyettesítsük
                              content = content.replace(entirePattern, replacement);
                              console.log('Sikeres direkt helyettesítés');
                            }
                          } catch (e) {
                            console.warn('Hiba a direkt helyettesítés során:', e);
                          }
                        }
                      }
                    }
                  }
                }
              }
              
              // Ha ezek után még mindig van benne helyettesítetlen mező, próbáljuk a speciális keresést
              if (content && content.includes('{{Szerződés kezdete}}') && content.includes('{{Szerződés vége}}')) {
                // Találjuk meg az összes előfordulás pozícióját
                const kezdetMatchek = Array.from(content.matchAll(/\{\{Szerződés kezdete\}\}/g));
                const vegeMatchek = Array.from(content.matchAll(/\{\{Szerződés vége\}\}/g));
                
                const kezdetIndexek = kezdetMatchek.map(m => m.index || 0);
                const vegeIndexek = vegeMatchek.map(m => m.index || 0);
                
                console.log('Nem helyettesített mezők pozíciói:', 
                  { kezdet: kezdetIndexek, vege: vegeIndexek });
                
                // Kézi helyettesítés - minden talált kezdet+vége párra
                for (const kezdetIndex of kezdetIndexek) {
                  // Keressük meg a legközelebbi vége pozíciót
                  const vegeIndex = vegeIndexek.find(idx => idx > kezdetIndex);
                  if (vegeIndex === undefined) continue;
                  
                  // Ellenőrizzük, van-e közöttük 00:00-tól és 24:00-ig
                  const koztesSzoveg = content.substring(kezdetIndex, vegeIndex + 20);
                  console.log('Köztes szöveg:', koztesSzoveg);
                  
                  if (koztesSzoveg.includes('00:00') && koztesSzoveg.includes('24:00')) {
                    // Ez a képen látható minta - helyettesítsük közvetlenül
                    const vegePos = content.indexOf('24:00-ig', vegeIndex);
                    if (vegePos > -1) {
                      const teljesResz = content.substring(
                        kezdetIndex, 
                        vegePos + 7
                      );
                      console.log('Teljes rész:', teljesResz);
                      
                      // Direkt string helyettesítés
                      content = content.replace(
                        teljesResz,
                        `${szerzodesKezdete} 00:00-tól ${szerzodesvege} 24:00-ig`
                      );
                    }
                  }
                }
              }
            }
            
            // Minden mintázatot próbáljunk meg
            for (const pattern of patterns) {
              content = content.replace(pattern.regex, pattern.replacement);
            }
            
            // Ellenőrizzük, hogy maradt-e még helyettesítetlen szerződéses mező
            if (content.includes('{{Szerződés kezdete}}') || content.includes('{{Szerződés vége}}') ||
                content.includes('{{ Szerződés kezdete }}') || content.includes('{{ Szerződés vége }}')) {
              console.warn('Figyelem: A szerződéses időtartam mezők nem minden előfordulása lett helyettesítve!');
              // Naplózzuk a problémás részt
              const problemIndex = content.indexOf('{{Szerződés kezdete}}');
              if (problemIndex > -1) {
                console.warn('Problémás rész:', content.substring(Math.max(0, problemIndex - 50), Math.min(content.length, problemIndex + 150)));
              }
            }
          }
        }

        // Speciális kezelés a szerződéssel kapcsolatos mezőknek
        // Ezeket a mezőket különös figyelemmel kezeljük, hogy biztosan minden előfordulásuk cserélve legyen
        criticalFields.forEach(field => {
          if (processedData[field] && content) {
            // Alap formátumok
            const patterns = [
              `{{${field}}}`,
              `{{ ${field} }}`,
              `{{${field} }}`,
              `{{ ${field}}}`
            ];
            
            // Minden lehetséges mintázatban kicseréljük
            patterns.forEach(pattern => {
              // Globális csere minden előfordulásra
              const escapedPattern = pattern.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
              const replaceRegex = new RegExp(escapedPattern, 'g');
              if (content) {
                content = content.replace(replaceRegex, processedData[field]);
              }
            });
            
            // Ellenőrizzük, maradt-e helyettesítetlen placeholder ezekhez a mezőkhöz
            if (content.includes(`{{${field}}}`) || content.includes(`{{ ${field} }}`)) {
              console.warn(`Figyelem: A '${field}' mező nem minden előfordulása lett helyettesítve!`);
            }
          }
        });

        // Globális cserék az összes előfordulásra
        if (content) {
          for (const [key, value] of Object.entries(processedData)) {
            // Reguláris kifejezéssel cseréljük az összes előfordulást
            const searchRegex = new RegExp(`\\{\\{${key}\\}\\}`, 'g');
            const searchRegexWithSpace = new RegExp(`\\{\\{ ${key} \\}\\}`, 'g');
            
            // Biztosítsuk, hogy a value definiálva van
            let safeValue = value === undefined || value === null ? '' : value;
            
            // Biztonsági ellenőrzés: kódoljuk az ampersand karaktereket ha szükséges
            if (typeof safeValue === 'string' && safeValue.includes('&') && !safeValue.includes('&amp;')) {
              safeValue = safeValue.replace(/&/g, '&amp;');
            }
            
            // Globális cserék az összes előfordulásra
            content = content.replace(searchRegex, safeValue);
            content = content.replace(searchRegexWithSpace, safeValue);
          }

          // Második kör: keressünk bármilyen {{...}} jelölőt és próbáljuk meg behelyettesíteni
          // a szöveg alapján (ez akkor is működik, ha XML tagek vannak közben)
          const placeholderRegex = /\{\{([^{}]+)\}\}/g;
          let match;

          // Használjunk while loopot, hogy minden előfordulást feldolgozzunk
          while ((match = placeholderRegex.exec(content)) !== null) {
            const fullMatch = match[0];  // A teljes egyezés, pl. {{kulcs}}
            const placeholder = match[1].trim(); // A kulcs, pl. "kulcs"

            // Tisztítsuk meg az XML tagektől
            const cleanPlaceholder = placeholder.replace(/<[^>]+>/g, '').trim();

            // Keressünk hozzá értéket
            let replacementValue = '';

            // Elsőként pontos egyezés
            if (processedData[cleanPlaceholder] !== undefined) {
              replacementValue = processedData[cleanPlaceholder] || '';
            } else {
              // Ha nincs pontos egyezés, próbáljunk normalizálást
              const normalizedPlaceholder = cleanPlaceholder.toLowerCase().replace(/\s+/g, '');

              // Végigmegyünk az összes data kulcson és próbálunk egyezést találni
              for (const [key, value] of Object.entries(processedData)) {
                const normalizedKey = key.toLowerCase().replace(/\s+/g, '');

                if (normalizedKey === normalizedPlaceholder ||
                  normalizedPlaceholder.includes(normalizedKey) ||
                  normalizedKey.includes(normalizedPlaceholder)) {
                  replacementValue = value || '';
                  break;
                }
              }
            }

            // Biztosítsuk, hogy az ampersand karakterek helyesen legyenek kódolva
            if (typeof replacementValue === 'string' && replacementValue.includes('&') && !replacementValue.includes('&amp;')) {
              replacementValue = replacementValue.replace(/&/g, '&amp;');
            }

            // Globális csere minden előfordulásra
            const escapedMatch = fullMatch.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            const replaceRegex = new RegExp(escapedMatch, 'g');
            content = content.replace(replaceRegex, replacementValue);
            
            // Visszaállítjuk a pozíciót az első egyezés utánra
            placeholderRegex.lastIndex = 0;
          }

          // Végső ellenőrzés - maradt-e helyettesítetlen placeholder?
          const remainingPlaceholders = content.match(/\{\{[^{}]+\}\}/g);
          if (remainingPlaceholders && remainingPlaceholders.length > 0) {
            console.warn(`Figyelem: Helyettesítetlen placeholderek (${remainingPlaceholders.length} db): ${remainingPlaceholders.slice(0, 5).join(', ')}${remainingPlaceholders.length > 5 ? '...' : ''}`);
          }
        }

        // A kritikus mezők legyenek cserélve, függetlenül a mintázattól
        if (content) {
          // Végső ellenőrzés - ha maradt még {{Szerződés...}} jellegű helyettesítetlen mező,
          // akkor azokat próbáljuk meg helyettesíteni teljes-szöveges kereséssel
          const finalCheck = (fieldName: string, value: string) => {
            if (content!.includes(`{{${fieldName}}}`)) {
              console.log(`Végső helyettesítés: ${fieldName} = ${value}`);
              
              // Biztosítsuk, hogy az ampersand karakterek helyesen legyenek kódolva
              let safeValue = value;
              if (typeof safeValue === 'string' && safeValue.includes('&') && !safeValue.includes('&amp;')) {
                safeValue = safeValue.replace(/&/g, '&amp;');
              }
              
              // Globális helyettesítés, minden formátumban
              const patterns = [
                new RegExp(`\\{\\{${fieldName}\\}\\}`, 'g'),        // {{Mezőnév}}
                new RegExp(`\\{\\{ ${fieldName} \\}\\}`, 'g'),      // {{ Mezőnév }}
                new RegExp(`\\{\\{${fieldName} \\}\\}`, 'g'),       // {{Mezőnév }}
                new RegExp(`\\{\\{ ${fieldName}\\}\\}`, 'g')        // {{ Mezőnév}}
              ];
              
              patterns.forEach(pattern => {
                content = content!.replace(pattern, safeValue);
              });
            }
          };
          
          // Menjünk végig minden kritikus mezőn
          criticalFields.forEach(field => {
            if (processedData[field]) {
              finalCheck(field, processedData[field]);
            }
          });

          // Speciális feldolgozás szerződés vége + 24:00 formátumhoz
          if (content.includes('{{Szerződés vége}}') && content.includes('24:00')) {
            console.log('Végső kísérlet a szerződésvége + 24:00 formátumhoz');
            
            // Keresd meg az összes előfordulás indexét
            const matches = Array.from(content.matchAll(/\{\{Szerződés vége\}\}/g));
            for (const match of matches) {
              const index = match.index;
              if (index === undefined) continue;
              
              // Keress a környezetében 24:00-t
              const context = content.substring(
                Math.max(0, index - 30),
                Math.min(content.length, index + 50)
              );
              
              if (context.includes('24:00')) {
                console.log('Kontextus a Szerződés vége körül:', context);
                
                // Próbáljuk meg kicserélni a teljes részt
                const szerzVegeStr = '{{Szerződés vége}}';
                const vegeMintaStart = Math.max(0, index - 5);
                const vegeMintaEnd = Math.min(content.length, index + szerzVegeStr.length + 30);
                const vegeMinta = content.substring(vegeMintaStart, vegeMintaEnd);
                
                // Építsük fel a helyettesítő mintát
                const placeholder = `{{Szerződés vége}}`;
                if (vegeMinta.includes(placeholder)) {
                  const replacement = processedData['Szerződés vége'] || '';
                  
                  // Biztosítsuk, hogy az ampersand karakterek helyesen legyenek kódolva
                  let safeReplacement = replacement;
                  if (typeof safeReplacement === 'string' && safeReplacement.includes('&') && !safeReplacement.includes('&amp;')) {
                    safeReplacement = safeReplacement.replace(/&/g, '&amp;');
                  }
                  
                  console.log(`Végső direkt csere: "${placeholder}" -> "${safeReplacement}"`);
                  content = content.replace(placeholder, safeReplacement);
                }
              }
            }
          }
        }

        // Végső ellenőrzés - maradt-e helyettesítetlen placeholder?
        const remainingPlaceholders = content && content.match(/\{\{[^{}]+\}\}/g);
        if (remainingPlaceholders && remainingPlaceholders.length > 0) {
          console.warn(`Figyelem: Helyettesítetlen placeholderek (${remainingPlaceholders.length} db): ${remainingPlaceholders.slice(0, 5).join(', ')}${remainingPlaceholders.length > 5 ? '...' : ''}`);
        }

        // Frissítjük a fájlt a módosított tartalommal
        docx.file(xmlFileName, content);
      }

      // Elkészítjük a dokumentumokat a kért formátumokban
      const result: Record<string, Blob> = {};

      // Mindig generáljuk a DOCX-et (akár kell, akár nem, mert a PDF generáláshoz szükséges)
      const docxBlob = await docx.generateAsync({ type: 'blob' });
      const docxOutput = new Blob([docxBlob], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      });

      // Ha kell DOCX, adjuk hozzá az eredményhez
      if (formats.includes('docx')) {
        result['docx'] = docxOutput;
      }

      // Ha kell PDF, konvertáljuk a DOCX-et a Cloud Run szolgáltatással
      if (formats.includes('pdf')) {
        try {
          console.log('PDF generálás megkezdése Cloud Run szolgáltatással...');
          const pdfBlob = await this.convertDocxToPdfWithGoogleDocs(docxOutput, fileName);
          result['pdf'] = pdfBlob;
          console.log('PDF generálás kész');
        } catch (error) {
          console.error('Hiba a PDF generálás során:', error);
          // Csak naplózzuk a hibát, de ne dobjunk kivételt, hogy a DOCX formátum még működjön
          console.warn('A PDF formátum generálása sikertelen volt, csak DOCX áll rendelkezésre');
        }
      }

      return result;
    } catch (error) {
      console.error('Hiba a dokumentum generálása közben:', error);
      throw error;
    }
  }
} 